import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'

import {
  Heading1,
  Heading2,
  Paragraph,
  Section,
  Section1,
  Section2,
  Wrapper
} from '@/features/Listings/seo/elements'
import AmenitiesFilterContent, {
  isAmenityFilterPage
} from '@/features/Listings/seo/search-results-content/content/amenities-filter-content'
import PopularCities from '@/features/Listings/seo/search-results-content/content/popular-cities'
import PopularFilters from '@/features/Listings/seo/search-results-content/content/popular-filters'
import PopularUniversities from '@/features/Listings/seo/search-results-content/content/popular-universities'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'
import UtilitiesFilterContent, {
  isUtilityFilterPage
} from '@/features/Listings/seo/search-results-content/content/utilities-filter-content'
import { toTitleCase } from '@/features/Listings/seo/search-results-content/content/utils'

const ContentTemplate = ({
  content,
  filterSlug,
  isCanadaPage = false,
  cityName,
  cityNameWithState,
  citySlug,
  state,
  showPopularUniversities = true
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const isChinese = router.locale === 'zh'
  const { hasFilter, filterTitle, filterName } = useFilterSlug(filterSlug)
  const showAmenityFilterContent = hasFilter && isAmenityFilterPage(filterSlug)
  const showUtilityFilterContent = hasFilter && isUtilityFilterPage(filterSlug)

  return (
    <Wrapper>
      {/* Marketing requested a different template for amenity filter pages: */}
      {showAmenityFilterContent && (
        <AmenitiesFilterContent
          state={state}
          citySlug={citySlug}
          cityName={cityName}
          filterTitle={filterTitle}
          filterSlug={filterSlug}
          isCanadaPage={isCanadaPage}
          hasFilter={hasFilter}
          filterName={filterName}
          cityNameWithState={cityNameWithState}
        />
      )}
      {showUtilityFilterContent && (
        <UtilitiesFilterContent
          state={state}
          citySlug={citySlug}
          cityName={cityName}
          filterTitle={filterTitle}
          filterSlug={filterSlug}
          isCanadaPage={isCanadaPage}
          cityNameWithState={cityNameWithState}
        />
      )}
      {!showAmenityFilterContent && !showUtilityFilterContent && (
        <>
          <Section>
            <Section1>
              <Section2>
                <Heading1>
                  <Trans
                    i18nKey="seo:About LOCATION"
                    values={{
                      location: cityNameWithState
                    }}
                    defaults="About {{ location }}"
                  />
                </Heading1>
                {content.about}
              </Section2>
            </Section1>
          </Section>
          <Section>
            <Section1>
              <Section2>
                <Heading1>
                  <Trans
                    i18nKey="seo:Renting in LOCATION"
                    values={{
                      location: cityNameWithState
                    }}
                    defaults="Renting in {{ location }}"
                  />
                </Heading1>
                {content.rentingIn}
              </Section2>
              <Section2>
                <Heading2>
                  <Trans
                    i18nKey="seo:Average Rent in LOCATION"
                    values={{
                      location: cityNameWithState
                    }}
                    defaults="Average Rent in {{ location }}"
                  />
                </Heading2>
                {content.averageRent}
              </Section2>
              <Section2>
                <Heading2>
                  {hasFilter ? (
                    <Trans
                      i18nKey="seo:Finding FILTER in LOCATION"
                      values={{
                        filter: filterName,
                        location: cityNameWithState
                      }}
                      defaults="Finding {{ filter }} in {{ location }}"
                    />
                  ) : (
                    <Trans
                      i18nKey="seo:Apartment Hunting in LOCATION"
                      values={{
                        location: cityNameWithState
                      }}
                      defaults="Apartment Hunting in {{ location }}"
                    />
                  )}
                </Heading2>
                {content.apartmentHunting}
              </Section2>
              <Section2>
                <Heading2>
                  {isCanadaPage ? (
                    t('seo:Property Rental Types')
                  ) : (
                    <Trans
                      i18nKey="seo:LOCATION Rental Types"
                      values={{
                        location: cityName
                      }}
                      defaults="{{ location }} Rental Types"
                    />
                  )}
                </Heading2>
                <Paragraph>
                  <Trans i18nKey="seo:Property Rental Types Paragraph">
                    There are many rental property types you can choose from in{' '}
                    {{ cityName }}.
                  </Trans>
                </Paragraph>
                {content.rentalTypes}
              </Section2>
              <Section2>
                <Heading2>
                  {isCanadaPage ? (
                    t('seo:Special Rental Types')
                  ) : (
                    <Trans
                      i18nKey="seo:Special LOCATION Rental Types"
                      values={{
                        location: cityName
                      }}
                      defaults="Special {{ location }} Rental Types"
                    />
                  )}
                </Heading2>
                <Paragraph>
                  <Trans i18nKey="seo:Special Rental Types Paragraph">
                    In addition to choosing your property type, there are also
                    specific rental types you can look into.
                  </Trans>
                </Paragraph>
                {content.specialTypes}
              </Section2>
              <Section2>
                <Heading2>
                  {isCanadaPage ? (
                    t('seo:Rental Amenities')
                  ) : (
                    <Trans
                      i18nKey="seo:LOCATION Amenities"
                      values={{
                        location: cityName
                      }}
                      defaults="{{ location }} Amenities"
                    />
                  )}
                </Heading2>
                {content.amenities}
              </Section2>
            </Section1>
          </Section>
          <Section>
            <Section1>
              <Section2>
                <Heading1>
                  <Trans
                    i18nKey="seo:Living in LOCATION"
                    values={{
                      location: cityNameWithState
                    }}
                    defaults="Living in {{ location }}"
                  />
                </Heading1>
                {content.living}
              </Section2>
              <Section2>
                <Heading2>
                  <Trans
                    i18nKey="seo:Popular Neighbourhoods in LOCATION"
                    values={{
                      location: cityName
                    }}
                    defaults="Popular Neighbourhoods in {{ location }}"
                  />
                </Heading2>
                {content.popularHoods}
              </Section2>
              <PopularCities
                state={state}
                citySlug={citySlug}
                location={cityName}
                isCanadaPage={isCanadaPage}
              />
              {showPopularUniversities && (
                <PopularUniversities
                  state={state}
                  locationName={cityName}
                  citySlug={citySlug}
                />
              )}
              <Section2>
                <Heading2>
                  <Trans
                    i18nKey="seo:Local Landmarks in LOCATION"
                    values={{
                      location: cityName
                    }}
                    defaults="Local Landmarks in {{ location }}"
                  />
                </Heading2>
                {content.landmarks}
              </Section2>
            </Section1>
          </Section>
          <Section>
            <Section1>
              <Section2>
                <Heading1>
                  {hasFilter ? (
                    <Trans
                      i18nKey="seo:FILTER LOCATION"
                      values={{
                        filter: toTitleCase(filterTitle),
                        location: cityName
                      }}
                      defaults="{{ filter }} {{ location }}"
                    />
                  ) : (
                    <Trans
                      i18nKey="seo:Apartments for Rent LOCATION"
                      values={{
                        location: cityName
                      }}
                      defaults="Apartments for Rent {{ location }}"
                    />
                  )}
                </Heading1>
                <Paragraph>
                  {hasFilter ? (
                    <Trans
                      i18nKey="seo:Search FILTER in LOCATION Paragraph"
                      values={{
                        filter: filterName,
                        location: cityNameWithState
                      }}
                      defaults="Search for condos, townhouses, rooms, and houses for rent in {{ location }} on liv.rent. Finding {{ filter }} near you has never been easier. Filter your search by price range (great for finding cheap apartments for rent), unfurnished or furnished apartments for rent, pet-friendly rentals, availability, and housing type. Options range from studio apartments and suites for rent to 1 or 2 bedroom apartment and 3+ bedrooms for rent."
                    />
                  ) : (
                    <Trans
                      i18nKey="seo:Apartments for Rent LOCATION Paragraph"
                      values={{
                        location: cityNameWithState
                      }}
                      defaults="Search for condos, townhouses, rooms, and houses for rent in {{ location }} on liv.rent. Finding apartments for rent near you has never been easier. Filter your search by price range (great for finding cheap apartments for rent), unfurnished or furnished apartments for rent, pet-friendly rentals, availability, and housing type. Options range from studio apartments and suites for rent to 1 or 2 bedroom apartment and 3+ bedrooms for rent."
                    />
                  )}
                </Paragraph>
                <Paragraph>
                  <Trans
                    i18nKey="seo:b.listing.view.our_rental_listings_date.title"
                    values={{
                      cityName: cityName,
                      currentDate: new Intl.DateTimeFormat(
                        isChinese ? 'zh-CN' : 'en-US',
                        {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }
                      ).format(new Date())
                    }}
                    defaults="Our {{ cityName }} rental listings are updated in real-time and are current as of {{ currentDate }}."
                  />
                </Paragraph>
              </Section2>
            </Section1>
          </Section>
          <Section>
            <PopularFilters
              isCanadaPage={isCanadaPage}
              citySlug={citySlug}
              cityName={cityName}
              cityNameWithState={cityNameWithState}
              hasFilter={hasFilter}
              filterName={filterName}
            />
          </Section>
        </>
      )}
    </Wrapper>
  )
}

export default ContentTemplate
